<template>
  <div class="customers-management">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <h2>客户管理</h2>
          <el-button type="primary" @click="handleCreate" :disabled="!canEditCustomers">
            <el-icon><Plus /></el-icon> 新增客户
          </el-button>
        </div>
      </template>

      <!-- 搜索和筛选区域 -->
      <div class="search-section">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索客户编号、名称、全名…"
          @keyup.enter="loadCustomers"
          style="width: 300px"
        >
          <template #append>
            <el-button @click="loadCustomers">
              <el-icon><Search /></el-icon>
            </el-button>
          </template>
        </el-input>

        <el-select
          v-model="filterActive"
          placeholder="状态筛选"
          @change="loadCustomers"
          style="width: 120px; margin-left: 10px"
        >
          <el-option label="全部" value="" />
          <el-option label="启用" :value="true" />
          <el-option label="禁用" :value="false" />
        </el-select>

        <el-button @click="resetFilters" style="margin-left: 10px">
          <el-icon><Refresh /></el-icon> 重置
        </el-button>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="tableData"
        v-loading="loading"
        :default-sort="{ prop: 'created_at', order: 'descending' }"
        style="width: 100%"
      >
        <el-table-column prop="customer_code" label="客户编号" width="120" sortable />
        <el-table-column prop="customer_name" label="客户名称" width="150" sortable />
        <el-table-column prop="customer_full_name" label="客户全名" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            <span>{{ row.customer_full_name || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="customer_group" label="客户分组" width="120">
          <template #default="{ row }">
            <el-tag v-if="row.customer_group" type="info" size="small">
              {{ row.customer_group }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="owner_users_label" label="归属业务员" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            <span>{{ row.owner_users_label || row.owner_user_name || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="is_active" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'danger'" size="small">
              {{ row.is_active ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="is_locked" label="锁定" width="88">
          <template #default="{ row }">
            <el-tag v-if="row.is_locked" type="warning" size="small">已锁定</el-tag>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="150" sortable>
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="更新时间" width="150" sortable>
          <template #default="{ row }">
            {{ formatDate(row.updated_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="420" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleView(row)">查看</el-button>
            <el-button
              size="small"
              type="info"
              plain
              @click="openDiscountPolicies(row)"
            >
              折扣策略
            </el-button>
            <el-button
              size="small"
              type="primary"
              @click="handleEdit(row)"
              :disabled="!canEditCustomers || row.is_locked"
            >
              编辑
            </el-button>
            <el-button
              size="small"
              type="warning"
              plain
              @click="handleSetLocked(row, !row.is_locked)"
              :disabled="!canLockCustomers"
            >
              {{ row.is_locked ? '解锁' : '锁定' }}
            </el-button>
            <el-button
              size="small"
              type="danger"
              plain
              @click="handleDelete(row)"
              :disabled="!canDeleteCustomers"
            >
              删除
            </el-button>
            <el-button
              size="small"
              :type="row.is_active ? 'danger' : 'success'"
              @click="handleToggleStatus(row)"
              :disabled="!canEditCustomers || row.is_locked"
            >
              {{ row.is_active ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页控件 -->
      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadCustomers"
          @current-change="loadCustomers"
        />
      </div>
    </el-card>

    <!-- 客户详情对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="showDetailDialog"
      width="800px"
      :fullscreen="isMobile"
    >
      <el-tabs v-model="activeTab" type="card">
        <!-- 基本信息标签页 -->
        <el-tab-pane label="基本信息" name="basic">
          <el-form :model="currentCustomer" label-width="120px" disabled>
            <el-form-item label="客户编号">
              <el-input v-model="currentCustomer.customer_code" />
            </el-form-item>
            <el-form-item label="客户名称">
              <el-input v-model="currentCustomer.customer_name" />
            </el-form-item>
            <el-form-item label="客户全名">
              <el-input v-model="currentCustomer.customer_full_name" placeholder="对账单/送货单抬头；空则与名称相同" />
            </el-form-item>
            <el-form-item label="客户分组">
              <el-input v-model="currentCustomer.customer_group" />
            </el-form-item>
            <el-form-item label="内部备注">
              <el-input
                v-model="currentCustomer.internal_notes"
                type="textarea"
                :rows="3"
              />
            </el-form-item>
            <el-form-item label="归属业务员">
              <el-input :value="currentCustomer.owner_users_label || currentCustomer.owner_user_name || '-'" />
            </el-form-item>
            <el-form-item label="状态">
              <el-switch v-model="currentCustomer.is_active" />
            </el-form-item>
            <el-form-item label="锁定">
              <el-tag v-if="currentCustomer.is_locked" type="warning" size="small">已锁定（需解锁后方可编辑）</el-tag>
              <span v-else>-</span>
            </el-form-item>
            <el-form-item label="请款模式">
              <el-input :value="currentCustomer.billing_policy?.billing_mode === 'line_level_delivery' ? '按项次请款' : '整单出库后请款'" />
            </el-form-item>
            <el-form-item label="允许预请款">
              <el-input :value="currentCustomer.billing_policy?.allow_pre_shipment ? '是' : '否'" />
            </el-form-item>
            <el-form-item label="CNY单价类型">
              <el-input :value="cnyPriceColumnLabel(currentCustomer.billing_policy?.sales_cny_price_column)" />
            </el-form-item>
            <el-form-item label="销售币别">
              <el-input :value="salesPriceCurrencyLabel(currentCustomer.billing_policy?.sales_price_currency)" />
            </el-form-item>
            <el-form-item label="创建时间">
              <el-input :value="formatDate(currentCustomer.created_at)" />
            </el-form-item>
            <el-form-item label="更新时间">
              <el-input :value="formatDate(currentCustomer.updated_at)" />
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 地址管理标签页 -->
        <el-tab-pane label="地址管理" name="addresses">
          <AddressManagement 
            ref="addressManagementRef"
            :customer-id="currentCustomer.customer_id"
            :edit-disabled="detailAddressEditDisabled"
            v-if="activeTab === 'addresses'"
          />
        </el-tab-pane>

        <!-- 联系人管理标签页 -->
        <el-tab-pane label="联系人管理" name="contacts">
          <ContactManagement 
            ref="contactManagementRef"
            :customer-id="currentCustomer.customer_id"
            :edit-disabled="detailContactEditDisabled"
            v-if="activeTab === 'contacts'"
          />
        </el-tab-pane>

        <el-tab-pane label="品牌门户" name="brandPortal">
          <CustomerBrandPortalTab
            v-if="activeTab === 'brandPortal' && currentCustomer.customer_id"
            :customer-id="currentCustomer.customer_id"
            :edit-disabled="detailBrandPortalEditDisabled"
          />
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- 新增/编辑客户对话框 -->
    <el-dialog
      :title="isEditing ? '编辑客户' : '新增客户'"
      v-model="showFormDialog"
      :width="isEditing ? '860px' : '600px'"
      @closed="resetForm"
    >
      <!-- 新增：单页主档 -->
      <template v-if="!isEditing">
        <el-form
          :model="formData"
          :rules="formRules"
          ref="formRef"
          label-width="100px"
        >
          <el-form-item label="客户编号">
            <el-input
              model-value="保存后自动生成（C0001 起）"
              disabled
            />
          </el-form-item>
          <el-form-item label="客户名称" prop="customer_name">
            <el-input
              v-model="formData.customer_name"
              placeholder="请输入客户名称"
            />
          </el-form-item>
          <el-form-item label="客户全名">
            <el-input
              v-model="formData.customer_full_name"
              placeholder="对账单/送货单抬头；可空，空则使用客户名称"
              maxlength="500"
              show-word-limit
            />
          </el-form-item>
          <el-form-item label="客户分组">
            <el-select
              v-model="formData.customer_group"
              placeholder="选择或输入分组"
              filterable
              allow-create
              clearable
              @blur="checkGroupSimilarity"
              style="width: 100%"
            >
              <el-option
                v-for="group in existingGroups"
                :key="group"
                :label="group"
                :value="group"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="内部备注">
            <el-input
              v-model="formData.internal_notes"
              type="textarea"
              placeholder="请输入内部备注"
              :rows="3"
            />
          </el-form-item>
          <el-form-item label="归属业务员">
            <el-select
              v-model="formData.owner_user_ids"
              multiple
              collapse-tags
              collapse-tags-tooltip
              filterable
              clearable
              placeholder="可多选不同岗位跟进人"
              style="width: 100%"
            >
              <el-option
                v-for="u in salesUserOptions"
                :key="u.user_id"
                :label="`${u.full_name || u.username} (${u.username})`"
                :value="u.user_id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="状态" prop="is_active">
            <el-switch v-model="formData.is_active" />
          </el-form-item>
          <el-form-item label="请款模式">
            <el-select v-model="formData.billing_policy.billing_mode" style="width: 100%">
              <el-option label="整单出库后请款" value="full_order_delivery" />
              <el-option label="按项次请款" value="line_level_delivery" />
            </el-select>
          </el-form-item>
          <el-form-item label="允许预请款">
            <el-switch v-model="formData.billing_policy.allow_pre_shipment" />
          </el-form-item>
          <el-form-item label="CNY单价类型">
            <el-select
              v-model="formData.billing_policy.sales_cny_price_column"
              clearable
              placeholder="人民币客户必填"
              style="width: 100%"
            >
              <el-option label="RMB现金单价" value="rmb_cash" />
              <el-option label="RMB月结含税" value="rmb_monthly_tax" />
            </el-select>
          </el-form-item>
          <el-form-item label="销售币别">
            <el-select v-model="formData.billing_policy.sales_price_currency" style="width: 100%">
              <el-option label="仅美金" value="USD" />
              <el-option label="仅人民币" value="CNY" />
              <el-option label="美金+人民币" value="BOTH" />
            </el-select>
          </el-form-item>
        </el-form>
      </template>

      <!-- 编辑：与查看一致的三标签（主档 + 地址 + 联系人） -->
      <template v-else>
        <el-tabs v-model="formActiveTab" type="card">
          <el-tab-pane label="基本信息" name="basic">
            <el-form
              :model="formData"
              :rules="formRules"
              ref="formRef"
              label-width="100px"
            >
              <el-form-item label="客户编号" prop="customer_code">
                <el-input
                  v-model="formData.customer_code"
                  placeholder="请输入客户编号"
                />
              </el-form-item>
              <el-form-item label="客户名称" prop="customer_name">
                <el-input
                  v-model="formData.customer_name"
                  placeholder="请输入客户名称"
                />
              </el-form-item>
              <el-form-item label="客户全名">
                <el-input
                  v-model="formData.customer_full_name"
                  placeholder="对账单/送货单抬头；可空，空则使用客户名称"
                  maxlength="500"
                  show-word-limit
                />
              </el-form-item>
              <el-form-item label="客户分组">
                <el-select
                  v-model="formData.customer_group"
                  placeholder="选择或输入分组"
                  filterable
                  allow-create
                  clearable
                  @blur="checkGroupSimilarity"
                  style="width: 100%"
                >
                  <el-option
                    v-for="group in existingGroups"
                    :key="group"
                    :label="group"
                    :value="group"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="内部备注">
                <el-input
                  v-model="formData.internal_notes"
                  type="textarea"
                  placeholder="请输入内部备注"
                  :rows="3"
                />
              </el-form-item>
              <el-form-item label="归属业务员">
                <el-select
                  v-model="formData.owner_user_ids"
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  filterable
                  clearable
                  placeholder="可多选不同岗位跟进人"
                  style="width: 100%"
                >
                  <el-option
                    v-for="u in salesUserOptions"
                    :key="'edit-' + u.user_id"
                    :label="`${u.full_name || u.username} (${u.username})`"
                    :value="u.user_id"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="状态" prop="is_active">
                <el-switch v-model="formData.is_active" />
              </el-form-item>
              <el-form-item label="请款模式">
                <el-select v-model="formData.billing_policy.billing_mode" style="width: 100%">
                  <el-option label="整单出库后请款" value="full_order_delivery" />
                  <el-option label="按项次请款" value="line_level_delivery" />
                </el-select>
              </el-form-item>
              <el-form-item label="允许预请款">
                <el-switch v-model="formData.billing_policy.allow_pre_shipment" />
              </el-form-item>
              <el-form-item label="CNY单价类型">
                <el-select
                  v-model="formData.billing_policy.sales_cny_price_column"
                  clearable
                  placeholder="人民币客户必填"
                  style="width: 100%"
                >
                  <el-option label="RMB现金单价" value="rmb_cash" />
                  <el-option label="RMB月结含税" value="rmb_monthly_tax" />
                </el-select>
              </el-form-item>
              <el-form-item label="销售币别">
                <el-select v-model="formData.billing_policy.sales_price_currency" style="width: 100%">
                  <el-option label="仅美金" value="USD" />
                  <el-option label="仅人民币" value="CNY" />
                  <el-option label="美金+人民币" value="BOTH" />
                </el-select>
              </el-form-item>
            </el-form>
          </el-tab-pane>
          <el-tab-pane label="地址管理" name="addresses">
            <AddressManagement
              v-if="formActiveTab === 'addresses' && formData.customer_id"
              ref="formAddressManagementRef"
              :customer-id="formData.customer_id"
              :edit-disabled="formAddressEditDisabled"
            />
          </el-tab-pane>
          <el-tab-pane label="联系人管理" name="contacts">
            <ContactManagement
              v-if="formActiveTab === 'contacts' && formData.customer_id"
              ref="formContactManagementRef"
              :customer-id="formData.customer_id"
              :edit-disabled="formContactEditDisabled"
            />
          </el-tab-pane>
          <el-tab-pane label="品牌门户" name="brandPortal">
            <CustomerBrandPortalTab
              v-if="formActiveTab === 'brandPortal' && formData.customer_id"
              :customer-id="formData.customer_id"
              :edit-disabled="formBrandPortalEditDisabled"
            />
          </el-tab-pane>
        </el-tabs>
      </template>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showFormDialog = false">取消</el-button>
          <el-button type="primary" @click="submitForm">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <PaymentDiscountPolicyPanel
      v-model:visible="discountPolicyVisible"
      :customer-id="discountPolicyCustomer?.customer_id"
      :customer-name="discountPolicyCustomer?.customer_name"
      :can-edit="canEditCustomers"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Search, Refresh } from '@element-plus/icons-vue'
import AddressManagement from '@/components/customer/AddressManagement.vue'
import ContactManagement from '@/components/customer/ContactManagement.vue'
import CustomerBrandPortalTab from '@/components/customer/CustomerBrandPortalTab.vue'
import PaymentDiscountPolicyPanel from '@/components/customer/PaymentDiscountPolicyPanel.vue'
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  setCustomerLocked
} from '@/api/customers'
import { getUsers } from '@/api/users'
import { hasPermission } from '@/utils/permission'
import { setOrderCreatePendingPick } from '@/utils/orderCreateDraft.js'
import { useTabs } from '@/composables/useTabs'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const searchKeyword = ref('')
const filterActive = ref('')
const showDetailDialog = ref(false)
const showFormDialog = ref(false)
const activeTab = ref('basic')
const formActiveTab = ref('basic')
const formRef = ref()
const isEditing = ref(false)
const isMobile = ref(window.innerWidth < 768)
const currentUser = ref(JSON.parse(localStorage.getItem('user') || '{}'))
const canEditCustomers = computed(() => hasPermission(currentUser.value, 'customers.edit'))
const canDeleteCustomers = computed(() => hasPermission(currentUser.value, 'customers.records.delete'))
const canLockCustomers = computed(() => hasPermission(currentUser.value, 'customers.records.lock'))

// 子组件引用
const addressManagementRef = ref()
const contactManagementRef = ref()
const formAddressManagementRef = ref()
const formContactManagementRef = ref()
const discountPolicyVisible = ref(false)
const discountPolicyCustomer = ref(null)

const canEditAddresses = computed(() => hasPermission(currentUser.value, 'customers.addresses.edit'))
const canEditContacts = computed(() => hasPermission(currentUser.value, 'customers.contacts.edit'))
/** 查看弹窗：无地址编辑权或客户已锁定时禁止改地址 */
const detailAddressEditDisabled = computed(
  () => !!currentCustomer.value?.is_locked || !canEditAddresses.value
)
const detailContactEditDisabled = computed(
  () => !!currentCustomer.value?.is_locked || !canEditContacts.value
)
const detailBrandPortalEditDisabled = computed(
  () => !!currentCustomer.value?.is_locked || !canEditCustomers.value
)
/** 编辑弹窗：子表权限与锁定 */
const formAddressEditDisabled = computed(() => !!formData.is_locked || !canEditAddresses.value)
const formContactEditDisabled = computed(() => !!formData.is_locked || !canEditContacts.value)
const formBrandPortalEditDisabled = computed(() => !!formData.is_locked || !canEditCustomers.value)

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 表格数据
const tableData = ref([])
const currentCustomer = ref({})
const existingGroups = ref([])
const salesUserOptions = ref([])

// 表单数据
const formData = reactive({
  customer_id: null,
  customer_code: '',
  customer_name: '',
  customer_full_name: '',
  customer_group: '',
  internal_notes: '',
  owner_user_id: null,
  owner_user_ids: [],
  is_active: true,
  is_locked: false,
  billing_policy: {
    billing_mode: 'full_order_delivery',
    allow_pre_shipment: false,
    require_pre_shipment_approval: true,
    sales_cny_price_column: null,
    sales_price_currency: 'BOTH'
  }
})

// 新增时不包含 customer_code 规则；编辑时编号必填
const formRules = computed(() => {
  const rules = {
    customer_name: [
      { required: true, whitespace: true, message: '请输入客户名称', trigger: 'blur' }
    ]
  }
  if (isEditing.value) {
    rules.customer_code = [
      { required: true, message: '请输入客户编号', trigger: 'blur' }
    ]
  }
  return rules
})

// 计算属性
const dialogTitle = computed(() => {
  if (!currentCustomer.value.customer_name) return '客户详情'
  return `${currentCustomer.value.customer_name} - 详情`
})

// 生命周期
onMounted(() => {
  loadCustomers()
  loadSalesUsers()
  window.addEventListener('resize', checkMobile)
  if (route.query.fromOrderCreate === '1') {
    nextTick(() => {
      if (canEditCustomers.value) handleCreate()
    })
  }
})

const loadSalesUsers = async () => {
  try {
    const response = await getUsers({ per_page: 1000, is_active: 'all' })
    if (response?.success) {
      salesUserOptions.value = (response.data || []).filter(u => u.is_active)
    }
  } catch (_) {
    // Ignore owner option preload failure so the page can still render customer data.
  }
}

// 检查是否为移动设备
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

// 加载客户数据
const loadCustomers = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.currentPage,
      per_page: pagination.pageSize,
      search: searchKeyword.value
    }

    // 只有当明确选择启用或禁用时才添加参数
    if (filterActive.value === true || filterActive.value === false) {
      params.is_active = filterActive.value
    }

    const response = await getCustomers(params)
    tableData.value = response.data
    pagination.total = response.total
    
    // 收集所有分组用于智能提示
    extractExistingGroups(response.data)
  } catch (error) {
    ElMessage.error('加载客户列表失败')
  } finally {
    loading.value = false
  }
}

// 提取现有分组
const extractExistingGroups = (customers) => {
  const groups = new Set()
  customers.forEach(customer => {
    if (customer.customer_group) {
      groups.add(customer.customer_group)
    }
  })
  existingGroups.value = Array.from(groups)
}

// 检查分组相似性
const checkGroupSimilarity = () => {
  const inputGroup = formData.customer_group
  if (!inputGroup) return

  const similarGroup = existingGroups.value.find(group => 
    group.toLowerCase().includes(inputGroup.toLowerCase()) ||
    inputGroup.toLowerCase().includes(group.toLowerCase())
  )

  if (similarGroup && similarGroup !== inputGroup) {
    ElMessageBox.confirm(
      `检测到已有相似分组 "${similarGroup}"，是否选择现有分组？`,
      '分组提示',
      {
        confirmButtonText: '使用现有分组',
        cancelButtonText: '新建分组',
        type: 'warning'
      }
    ).then(() => {
      formData.customer_group = similarGroup
    }).catch(() => {
      // 用户选择新建分组，不做任何操作
    })
  }
}

// 重置筛选条件
const resetFilters = () => {
  searchKeyword.value = ''
  filterActive.value = ''
  pagination.currentPage = 1
  loadCustomers()
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('zh-CN')
}

// 查看客户详情
const handleView = (row) => {
  currentCustomer.value = { ...row }
  activeTab.value = 'basic'
  showDetailDialog.value = true
}

const openDiscountPolicies = (row) => {
  discountPolicyCustomer.value = { ...row }
  discountPolicyVisible.value = true
}

// 打开新增对话框
const handleCreate = () => {
  if (!canEditCustomers.value) {
    ElMessage.warning('你没有新增客户权限')
    return
  }
  isEditing.value = false
  showFormDialog.value = true
}

// 打开编辑对话框
const handleEdit = (row) => {
  if (!canEditCustomers.value) {
    ElMessage.warning('你没有编辑客户权限')
    return
  }
  isEditing.value = true
  formActiveTab.value = 'basic'
  Object.assign(formData, row)
  const ids = Array.isArray(row.owner_user_ids) && row.owner_user_ids.length
    ? row.owner_user_ids.map((v) => Number(v)).filter((v) => Number.isInteger(v) && v > 0)
    : (row.owner_user_id ? [Number(row.owner_user_id)] : [])
  formData.owner_user_ids = ids
  if (!formData.billing_policy) {
    formData.billing_policy = {
      billing_mode: 'full_order_delivery',
      allow_pre_shipment: false,
      require_pre_shipment_approval: true,
      sales_cny_price_column: null,
      sales_price_currency: 'BOTH'
    }
  } else if (!formData.billing_policy.sales_price_currency) {
    formData.billing_policy.sales_price_currency = 'BOTH'
  }
  showFormDialog.value = true
}

// 切换客户状态
const handleToggleStatus = async (row) => {
  if (!canEditCustomers.value) {
    ElMessage.warning('你没有修改客户状态权限')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定要${row.is_active ? '禁用' : '启用'}客户 "${row.customer_name}" 吗？`,
      '提示',
      { type: 'warning' }
    )
    
    // 直接使用 updateCustomer 函数
    await updateCustomer(row.customer_id, {
      customer_code: row.customer_code,
      customer_name: row.customer_name,
      customer_full_name: row.customer_full_name || undefined,
      customer_group: row.customer_group,
      internal_notes: row.internal_notes,
      is_active: !row.is_active
    })
    
    ElMessage.success('状态更新成功')
    loadCustomers()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('状态更新失败: ' + (error.response?.data?.message || error.message))
    }
  }
}

const handleDelete = async (row) => {
  if (!canDeleteCustomers.value) {
    ElMessage.warning('你没有删除客户权限')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定删除客户「${row.customer_name}」（${row.customer_code}）吗？删除后不可恢复。`,
      '删除客户',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
    )
    await deleteCustomer(row.customer_id)
    ElMessage.success('客户已删除')
    loadCustomers()
  } catch (error) {
    if (error === 'cancel') return
    const d = error.response?.data
    const msg =
      (d && typeof d === 'object' && d.message) ||
      (typeof d === 'string' ? d : null) ||
      error.message ||
      '删除失败'
    if (d?.hints?.length || String(msg).includes('【处理指引】')) {
      ElMessageBox.alert(msg, '无法删除客户', { confirmButtonText: '知道了' })
    } else {
      ElMessage.error(msg)
    }
  }
}

const handleSetLocked = async (row, locked) => {
  if (!canLockCustomers.value) {
    ElMessage.warning('你没有锁定/解锁客户权限')
    return
  }
  const verb = locked ? '锁定' : '解锁'
  try {
    await ElMessageBox.confirm(
      locked
        ? `确定锁定客户「${row.customer_name}」吗？锁定后即使有编辑权限也不能修改其资料、地址与联系人。`
        : `确定解锁客户「${row.customer_name}」吗？`,
      `${verb}客户`,
      { type: locked ? 'warning' : 'info', confirmButtonText: verb, cancelButtonText: '取消' }
    )
    const res = await setCustomerLocked(row.customer_id, locked)
    ElMessage.success(res.message || (locked ? '已锁定' : '已解锁'))
    if (showDetailDialog.value && currentCustomer.value?.customer_id === row.customer_id) {
      Object.assign(currentCustomer.value, res.data || {})
    }
    loadCustomers()
  } catch (error) {
    if (error === 'cancel') return
    const d = error.response?.data
    const msg =
      (d && typeof d === 'object' && d.message) ||
      (typeof d === 'string' ? d : null) ||
      error.message ||
      '操作失败'
    ElMessage.error(msg)
  }
}

// 提交表单
const submitForm = async () => {
  if (!canEditCustomers.value) {
    ElMessage.warning('你没有编辑客户权限')
    return
  }
  if (isEditing.value && formData.is_locked) {
    ElMessage.warning('客户已锁定，无法修改')
    return
  }
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    
    if (isEditing.value) {
      const payload = {
        ...formData,
        owner_user_ids: Array.isArray(formData.owner_user_ids) ? formData.owner_user_ids : []
      }
      await updateCustomer(formData.customer_id, payload)
      ElMessage.success('客户更新成功')
    } else {
      const payload = {
        customer_name: String(formData.customer_name ?? '').trim(),
        customer_full_name: String(formData.customer_full_name ?? '').trim() || undefined,
        customer_group: formData.customer_group || undefined,
        internal_notes: formData.internal_notes || undefined,
        owner_user_ids: Array.isArray(formData.owner_user_ids) ? formData.owner_user_ids : [],
        is_active: formData.is_active,
        billing_policy: formData.billing_policy
      }
      const response = await createCustomer(payload)
      const code = response?.data?.customer_code
      ElMessage.success(code ? `客户创建成功，编号 ${code}` : '客户创建成功')
      showFormDialog.value = false
      loadCustomers()
      if (route.query.fromOrderCreate === '1') {
        const id = response?.data?.customer_id
        if (id != null) setOrderCreatePendingPick({ customer_id: id })
        try {
          await ElMessageBox.confirm(
            '客户已保存。是否返回「订单管理」继续录入当前订单？',
            '返回订单',
            {
              type: 'success',
              confirmButtonText: '返回订单',
              cancelButtonText: '留在本页'
            }
          )
          await router.push({ path: '/orders', query: { resumeOrderDraft: '1' } })
          await nextTick()
          useTabs().removeTab('/customers')
        } catch {
          /* 留在客户管理：不关标签 */
        }
        return
      }
    }

    showFormDialog.value = false
    loadCustomers()
  } catch (error) {
    const d = error.response?.data
    const msg =
      (d && typeof d === 'object' && d.message) ||
      (typeof d === 'string' ? d : null) ||
      error.message ||
      '操作失败'
    ElMessage.error(msg)
  }
}

const cnyPriceColumnLabel = (val) => {
  if (val === 'rmb_cash') return 'RMB现金单价'
  if (val === 'rmb_monthly_tax') return 'RMB月结含税'
  return '未配置'
}

const salesPriceCurrencyLabel = (val) => {
  if (val === 'USD') return '仅美金'
  if (val === 'CNY') return '仅人民币'
  return '美金+人民币'
}

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(formData, {
    customer_id: null,
    customer_code: '',
    customer_name: '',
    customer_full_name: '',
    customer_group: '',
    internal_notes: '',
    owner_user_id: null,
    owner_user_ids: [],
    is_active: true,
    is_locked: false,
    billing_policy: {
      billing_mode: 'full_order_delivery',
      allow_pre_shipment: false,
      require_pre_shipment_approval: true
    }
  })
  isEditing.value = false
  formActiveTab.value = 'basic'
}
</script>

<style scoped>
.customers-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-muted {
  color: var(--el-text-color-secondary);
}

.search-section {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .search-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-section > * {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>